import { db } from '../idb';
import { policyFor, cacheState, stripBase } from './policy';

export type CacheResult<T> = {
  data: T;
  state: 'fresh' | 'stale' | 'expired';
  fetchedAt: number;
};

/**
 * Read from cache. Returns null if the URL has no policy or the entry is expired.
 * Always returns the data regardless of fresh/stale — caller decides whether to
 * trigger a background refetch (typically when state === 'stale').
 */
export async function readCache<T>(url: string): Promise<CacheResult<T> | null> {
  const path = stripBase(url);
  const policy = policyFor(path);
  if (!policy) return null;

  const handle = await db();
  const row = await handle.get('readCache', url);
  if (!row) return null;

  const state = cacheState(row.fetchedAt, policy);
  if (state === 'expired') {
    // Best-effort cleanup; don't block.
    handle.delete('readCache', url).catch(() => {});
    return null;
  }
  return { data: row.payload as T, state, fetchedAt: row.fetchedAt };
}

/**
 * Write to cache. Caller responsible for stamping after a successful network read.
 */
export async function writeCache<T>(url: string, payload: T): Promise<void> {
  const path = stripBase(url);
  if (!policyFor(path)) return; // unknown path → don't cache
  const handle = await db();
  await handle.put('readCache', { url, payload, fetchedAt: Date.now() });
}

/**
 * Invalidate cache entries after a mutation. A string pattern matches by
 * canonical path (so callers may pass `/driver-app/me/trips/:id` even though
 * keys are full URLs) OR by exact key; a RegExp tests the raw key. Used e.g.
 * after POST duty-slip to drop /me/trips/today + the trip detail.
 */
export async function invalidate(pattern: string | RegExp): Promise<void> {
  const handle = await db();
  const tx = handle.transaction('readCache', 'readwrite');
  let cursor = await tx.store.openCursor();
  while (cursor) {
    const key = cursor.key as string;
    const match =
      typeof pattern === 'string'
        ? key === pattern || stripBase(key) === stripBase(pattern)
        : pattern.test(key);
    if (match) await cursor.delete();
    cursor = await cursor.continue();
  }
  await tx.done;
}
